import { Injectable, EventEmitter, PLATFORM_ID, Inject } from '@angular/core';
import { AppDB } from './database';
import { Observable, Subject } from 'rxjs';
import { EVENTS, APP_INSTANCE_KEY } from '../constants';
import { SocketService } from './socket';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class EventService {

  readonly eventEmitter: EventEmitter<any> = new EventEmitter();

  private appUser = new Subject<any>();
  private appInstance = new Subject<any>();
  private appUserObservable: Observable<any>;
  private appInstanceObservable: Observable<any>;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private db: AppDB,
    public ws: SocketService) {
    this.appUserObservable = this.appUser.asObservable();
    this.appInstanceObservable = this.appInstance.asObservable();
  }

  publish(event: string, data: any) {
    this.eventEmitter.emit({ event, data });
  }

  listen() {
    if (isPlatformBrowser(this.platformId)) {
      this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
        if (!app) {
          this.eventEmitter.emit({ event: EVENTS.APP_INITIALIZED, data: true });
        } else if (app.user) {
          this.appInstance.next(app);
          this.appUser.next(app.user);
          this.ws.init(app.user.id);
        }
      }).catch(() => {
        this.eventEmitter.emit({ event: EVENTS.APP_INITIALIZED, data: true });
      });
      this.subscribe();
    }
  }

  get observeUser(): Observable<any> {
    return this.appUserObservable;
  }

  get observeInstance(): Observable<any> {
    return this.appInstanceObservable;
  }

  private subscribe() {
    this.eventEmitter.subscribe(
      ({ event, data }) => {
        switch (event) {
          case EVENTS.APP_INITIALIZED:
            this.appUser.next(null);
            this.db.appInstance.setItem(APP_INSTANCE_KEY, { id: 1 });
            break;
          case EVENTS.SESSION_START:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.auth = data.auth_token;
              app.user = data.user;
              app.admin = data.admin;
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
            }).catch(() => {
              this.db.appInstance.setItem(APP_INSTANCE_KEY, {
                id: 1,
                auth: data.auth_token,
                user: data.user,
                admin: data.admin
              });
            }).finally(() => {
              this.appInstance.next({ auth: data.auth_token, user: data.user, admin: data.admin });
              this.appUser.next(data.user);
              this.ws.init(data.user.id);
            });
            break;
          case EVENTS.SESSION_END:
            this.appUser.next(null);
            this.appInstance.next(null);
            this.db.appInstance.removeItem(APP_INSTANCE_KEY);
            this.db.appInstance.setItem(APP_INSTANCE_KEY, { id: 1 });
            this.db.appStorage.clear();
            this.ws.close();
            break;
          case EVENTS.USER_EMAIL_VERIFIED:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.user.email_verified = true;
              app.user.is_new = false;
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          case EVENTS.USER_PHONE_ADDED:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.user.phone = data.phone;
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          case EVENTS.USER_PHONE_VERIFIED:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.user.phone_verified = true;
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          case EVENTS.UPDATE_BANK_CARD:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              if (!app.user.debit_cards) {
                app.user.debit_cards = [];
              }
              app.user.debit_cards.push(data);
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          case EVENTS.UPDATE_USER:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.user = data;
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          case EVENTS.APP_USER:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              this.appUser.next(app.user);
              this.ws.init(app.user.id);
            }).catch(err => console.error(err));
            break;
          case EVENTS.ADMIN_PASSWORD_SET:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.user.password_set = true;
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          case EVENTS.ADMIN_SETUP_COMPLETE:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.user.is_new = false;
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          case EVENTS.UPDATE_BUSINESS:
            this.db.appInstance.getItem(APP_INSTANCE_KEY).then((app: any) => {
              app.user.business = { ...app.user.business, ...data.business };
              this.db.appInstance.setItem(APP_INSTANCE_KEY, app);
              this.appUser.next(app.user);
            }).catch(err => console.error(err));
            break;
          default:
            break;
        }
      }
    );
  }

}
